import type { AdjustmentMode } from '@local/lib';

import {
  AdjustmentMode as ApiAdjustmentMode,
  getDefaultAdjustmentModeFromSpecType,
  getHigherSpecEffect,
  getPercentageBasis,
  HigherSpecEffect,
  isQuantityFormulaType,
  PercentageBasis,
  PRICE_COMPONENTS,
  ProductPriceBasisType,
  QualityAdjustmentType,
  QualityFormulaType,
  resolvePriceComponentsTotal,
  SpecType,
  TierMode,
} from '@local/lib';

import type { SpecDefinition } from './qualityFormulaTypes';

export const SPEC_TYPES: SpecDefinition[] = [
  { hint: 'Non-combustible Mineral Residue.', icon: '🧱', key: SpecType.Ash, label: 'Ash' },
  { hint: 'Heat Energy Content.', icon: '🔥', key: SpecType.Btu, label: 'BTU' },
  { hint: 'Hardgrove Grindability Index.', icon: '⚙️', key: SpecType.Hgi, label: 'HGI' },
  { hint: 'Kilocalorie Energy Content.', icon: '🌡️', key: SpecType.Kcal, label: 'KCAL' },
  {
    hint: 'Water content — May drive a quantity or price adjustment.',
    icon: '💧',
    key: SpecType.Moisture,
    label: 'Moisture',
  },
  {
    hint: 'Nitrogen content — Billable quantity scales to the actual %.',
    icon: '🧪',
    key: SpecType.Nitrogen,
    label: 'Nitrogen',
  },
  { hint: 'Sulfur content.', icon: '☁️', key: SpecType.Sulfur, label: 'Sulfur' },
  { hint: 'Volatile matter content.', icon: '💨', key: SpecType.Volatile, label: 'Volatile Matter' },
];

export const FORMULA_TYPES: Array<{
  hint: string;
  label: string;
  value: QualityFormulaType;
}> = [
  {
    hint: 'Price moves by a % of the starting price, scaled to how far the actual value is from your contracted value.',
    label: 'Price: Percentage of Price',
    value: QualityFormulaType.Percentage,
  },
  {
    hint: 'Price moves by a fixed $ amount for each unit the actual value differs from your contracted value.',
    label: 'Price: Fixed Amount per Unit',
    value: QualityFormulaType.PerUnit,
  },
  {
    hint: 'Recalculates the whole price from the test-vs-contracted moisture ratio — one formula, no tiers.',
    label: 'Price: Price Adjustment to Contracted',
    value: QualityFormulaType.AdjustedSalesPrice,
  },
  {
    hint: 'Reduces the delivered quantity when the test moisture is higher than contracted — never increases it when lower.',
    label: 'Quantity: Actual to Contracted (Discount Only)',
    value: QualityFormulaType.QuantityAdjustedToContracted,
  },
  {
    hint: 'Converts delivered quantity to a dry-weight equivalent when the test moisture is higher than contracted — never increases it when lower.',
    label: 'Quantity: Actual to Dry to Contracted (Discount Only)',
    value: QualityFormulaType.QuantityAdjustedToDry,
  },
  {
    hint: 'Billable quantity = Shipped Quantity × (Actual % ÷ 100) — no contracted baseline, no premium or discount, always applies.',
    label: 'Quantity: Adjusted by Actual %',
    value: QualityFormulaType.QuantityAdjustedByPercent,
  },
];

/**
 * Looks up a formula type's Price/Quantity classification, deriving it from the shared
 * `isQuantityFormulaType` predicate (the same one the API's request schema and
 * `QualityAdjustmentManager` use) rather than a second, independently-maintained field on
 * `FORMULA_TYPES` — only one place can ever decide which formula types are quantity-type. Still
 * throws for a value missing from `FORMULA_TYPES` (the label/hint catalog) so a genuinely unknown
 * `QualityFormulaType` surfaces immediately instead of silently falling through to Price.
 */
export const getFormulaAdjustmentCategory = (formulaType: QualityFormulaType): QualityAdjustmentType => {
  const option = FORMULA_TYPES.find(candidate => candidate.value === formulaType);
  if (!option) throw new Error(`Unknown QualityFormulaType: ${formulaType}`);
  return isQuantityFormulaType(formulaType) ? QualityAdjustmentType.Quantity : QualityAdjustmentType.Price;
};

export const MULTI_ROW_FORMULA_TYPES = new Set<QualityFormulaType>([
  QualityFormulaType.Percentage,
  QualityFormulaType.PerUnit,
]);

/**
 * Type predicate for `MULTI_ROW_FORMULA_TYPES` membership — unlike a plain `Set.has()` check,
 * this narrows `formulaType` to the two tiered formula types at every call site, so indexing
 * `AMOUNT_CONFIG_BY_FORMULA_TYPE` (or passing `formulaType` to a tiered-only component) needs no
 * cast.
 */
export const isMultiRowFormulaType = (
  formulaType: QualityFormulaType,
): formulaType is QualityFormulaType.Percentage | QualityFormulaType.PerUnit =>
  MULTI_ROW_FORMULA_TYPES.has(formulaType);

export const ADJUSTMENT_MODES: Array<{ label: string; value: AdjustmentMode }> = [
  { label: 'Discount', value: ApiAdjustmentMode.Discount },
  { label: 'Premium', value: ApiAdjustmentMode.Premium },
];

// ── Product Price Basis (PERCENTAGE / "% of Price" formulas only) ───────────
// What a "% of Price" formula's percentFactor scales against — see ProductPriceBasisState in
// qualityFormulaTypes.ts and ProductPriceBasisSection.tsx for the compact Step 1 control built
// from this metadata.

/**
 * Short, business-friendly labels + hints for the three Product Price Basis options — the
 * component catalog a "Price Components" basis selects from lives in `@local/lib`'s
 * `PRICE_COMPONENTS` (shared with the API, which sums it — see `ProductPriceBasisSection.tsx`).
 */
export const PRODUCT_PRICE_BASIS_OPTIONS: Array<{ hint: string; label: string; value: ProductPriceBasisType }> = [
  {
    hint: 'Scales against the full calculated contract price — the total price charged to the customer.',
    label: 'Contract Price',
    value: ProductPriceBasisType.ContractPrice,
  },
  {
    hint: 'Scales against only the pricing-formula components you select below, not the full contract price.',
    label: 'Price Components',
    value: ProductPriceBasisType.PriceComponents,
  },
  {
    hint: 'Scales against a specific negotiated price you enter, overriding the contract price entirely for this spec.',
    label: 'Deemed Price',
    value: ProductPriceBasisType.DeemedPrice,
  },
];

/**
 * Keys into the shared `PRICE_COMPONENTS` catalog representing the components actually
 * established on this example contract (see `ContractPricingSummarySection`) — there is no live
 * per-contract breakdown yet (see that catalog's own doc comment), so the "Price Components"
 * Price Basis's Add Component picker is restricted to this list rather than the full catalog,
 * since a real contract only ever lets you scale against components it actually has. Once real
 * per-contract data exists, this list should come from the contract itself instead.
 */
export const CONTRACT_PRICE_COMPONENT_KEYS = ['ORIGIN_PRICE', 'FREIGHT', 'PREMIUMS'];

/** The example contract's identifier, shown in Contract Setup so it reads as a specific contract
 * rather than a generic one. */
export const CONTRACT_NUMBER = 'PRE-54365';

/** The example contract's unit of measure — shared so Contract Setup's price-per-unit display and
 * the Test Formula playground's Delivered Quantity field always read against the same unit. */
export const CONTRACT_UNIT_OF_MEASURE = 'MT';

/** The example contract's total price, summed from `CONTRACT_PRICE_COMPONENT_KEYS` so it can
 * never drift from the components Contract Setup displays — the Test Formula playground's
 * Starting Price defaults to this same value. */
export const CONTRACT_PRICE = resolvePriceComponentsTotal(CONTRACT_PRICE_COMPONENT_KEYS);

/** The shared `PRICE_COMPONENTS` catalog narrowed to `CONTRACT_PRICE_COMPONENT_KEYS`. */
export const CONTRACT_PRICE_COMPONENTS = PRICE_COMPONENTS.filter(component =>
  CONTRACT_PRICE_COMPONENT_KEYS.includes(component.key),
);

/** Default playground-editable value for every `CONTRACT_PRICE_COMPONENTS` entry — the Test
 * Formula playground starts each component's input at its catalog value, then lets it be edited
 * per `testComponentValues` (see `testValuesSlice`). */
export const getInitialTestComponentValues = (): Record<string, number> =>
  CONTRACT_PRICE_COMPONENTS.reduce<Record<string, number>>((acc, component) => {
    acc[component.key] = component.value;
    return acc;
  }, {});

/** Sums `keys`' signed values against the playground's own editable `testComponentValues` (an
 * unselected/unedited key falls back to its catalog value) — the Test Formula playground's
 * equivalent of the API's `resolvePriceComponentsTotal`, since nothing the playground edits is
 * ever sent to or resolved by the API itself (see `qualityFormulaRequest`'s own Price Components
 * override). */
export const resolveTestPriceComponentsTotal = (keys: string[], testComponentValues: Record<string, number>): number =>
  CONTRACT_PRICE_COMPONENTS.filter(component => keys.includes(component.key)).reduce(
    (total, component) => total + (testComponentValues[component.key] ?? component.value) * component.sign,
    0,
  );

/** The example contract's quantity, in `CONTRACT_UNIT_OF_MEASURE` units — the Test Formula
 * playground's Delivered Quantity defaults to this same value. */
export const CONTRACT_QUANTITY = 1000;

/** The example contract's shipping/delivery mode, shown alongside its quantity in Contract
 * Setup — purely descriptive, with no effect on any calculation. */
export const CONTRACT_MODE = 'Truck';

/**
 * How much product one shipment/load typically carries for a given transportation mode, in
 * `CONTRACT_UNIT_OF_MEASURE` units — e.g. one Truck load carries ~25 MT, one Rail car carries far
 * more. Falls back to `CONTRACT_QUANTITY` (a single load) for any mode without its own figure.
 */
export const CONTRACT_UNITS_PER_MODE: Record<string, number> = {
  Rail: 100,
  Truck: 25,
  Vessel: 25000,
};

/** How much product one shipment/load carries for `CONTRACT_MODE`, in `CONTRACT_UNIT_OF_MEASURE`
 * units — shown in Contract Setup as "Units per Mode" (e.g. "25 MT" for a Truck load). */
export const CONTRACT_UNIT_PER_MODE = CONTRACT_UNITS_PER_MODE[CONTRACT_MODE] ?? CONTRACT_QUANTITY;

/**
 * Shared Discount/Premium color coding — red for Discount, green for Premium — used everywhere
 * an adjustment mode is shown (Threshold side cards, Range row controls) so the two tier modes
 * read consistently.
 */
export const ADJUSTMENT_MODE_COLOR: Record<AdjustmentMode, string> = {
  [ApiAdjustmentMode.Discount]: 'red',
  [ApiAdjustmentMode.Premium]: 'green',
};

export const TIER_MODES: Array<{ hint: string; label: string; value: TierMode }> = [
  {
    hint: 'One or two rules anchored to a single contracted value, each with its own Premium or Discount adjustment.',
    label: 'Threshold',
    value: TierMode.Threshold,
  },
  {
    hint: 'An ordered chain of value ranges, each with its own Premium or Discount adjustment.',
    label: 'Range',
    value: TierMode.Range,
  },
];

/**
 * Business-friendly tooltip copy for terminology that stakeholder feedback flagged as unclear.
 * Centralized here so every field that references these concepts (row tiers, single-value specs)
 * shows identical wording instead of each component writing its own explanation.
 */
export const FIELD_HELP = {
  adjustmentMode:
    'Discount lowers the price (or reduces delivered quantity) when this tier applies — typically because the test result is worse than your contracted value. Premium raises the price when this tier applies — typically because the test result is better than contracted.',
  contractedValue:
    'The value entered in your contract. The actual test result is compared against this number to determine whether a discount or premium applies, and by how much.',
  flatAdjustment:
    'When checked, the full rate applies as soon as Actual is anywhere in this range — no scaling by how far past the range it is, and Per increment is ignored. When unchecked, the adjustment scales continuously: rate × (difference ÷ per increment).',
  formulaType:
    'How price or quantity should change: as a percentage of the starting price, a fixed dollar amount per unit of difference, or a full price recalculation based on contracted vs. actual value.',
  lowerBound:
    "The bottom of this tier's range. This tier is used whenever the actual test result is above this number — raise it to require a bigger difference from spec before this tier kicks in.",
  precision: 'How many decimal places the calculated price or quantity adjustment is rounded to.',
  priceBasisDeemedPrice:
    'A specific negotiated price to use instead of the contract price for this spec only — overrides the contract/component calculation basis.',
  priceBasisPriceComponents:
    'Values are pulled from the contract\u2019s pricing formula and can\u2019t be edited here \u2014 add the components this spec should scale against, and the Total Basis below updates automatically.',
  priceBasisType:
    'Which price this spec\'s "% of Price" formula is calculated against. Different specs can use different bases on the same contract.',
  tierMode:
    'Threshold anchors one or two rules to a single contracted value. Range instead configures an ordered chain of value ranges.',
  upperBound:
    "The top of this tier's range. This tier stops applying once the actual test result goes above this number. Check “No upper limit” if this tier should cover every higher value with no ceiling.",
};

/**
 * The three single-row formula types each store their one configurable value in a different row
 * field — shared by QualitySpecCard (rendering the field, and what a cleared input snaps back to)
 * and qualityFormulaDefaults (a freshly created row's starting value) so both stay in lockstep.
 */
export const SINGLE_ROW_CONFIG_BY_FORMULA_TYPE = {
  [QualityFormulaType.AdjustedSalesPrice]: {
    fallback: 8,
    field: 'contractedSpec' as const,
    hint: "The contracted moisture % (can't be exactly 100)",
    label: 'Contracted moisture (%)',
  },
  [QualityFormulaType.QuantityAdjustedToContracted]: {
    fallback: 8,
    field: 'lowerBound' as const,
    hint: 'Quantity is reduced only when the actual moisture value is higher than this',
    label: 'Contracted moisture (%)',
  },
  [QualityFormulaType.QuantityAdjustedToDry]: {
    fallback: 8,
    field: 'lowerBound' as const,
    hint: "Converts to dry weight only when the actual moisture value is higher than this (can't be exactly 100)",
    label: 'Contracted moisture (%)',
  },
};

/**
 * PERCENTAGE and PER_UNIT store their tier "amount" in different row fields — shared by
 * FormulaRowFields (rendering the field) and validateEnabledSpecStates for the same reason as
 * SINGLE_ROW_CONFIG_BY_FORMULA_TYPE above.
 */
export const AMOUNT_CONFIG_BY_FORMULA_TYPE = {
  [QualityFormulaType.Percentage]: {
    field: 'percentFactor' as const,
    hint: 'How strongly price reacts to each 1% of difference from spec — higher number = bigger price swing',
    label: 'Percent factor',
  },
  [QualityFormulaType.PerUnit]: {
    field: 'qualityPricePerUnit' as const,
    hint: 'Dollar amount added or subtracted for each unit the actual value differs from spec',
    label: 'Rate per Unit ($)',
  },
};

export const getSpecEffectCopy = (specType: SpecType) => {
  const isPremiumEffect = getHigherSpecEffect(specType) === HigherSpecEffect.Premium;
  return isPremiumEffect
    ? {
        aboveMeaning: 'better quality (Premium side)',
        belowMeaning: 'lower quality (Discount side)',
      }
    : {
        aboveMeaning: 'lower quality (Discount side)',
        belowMeaning: 'better quality (Premium side)',
      };
};

export const getSpecUnit = (specType: SpecType) =>
  getPercentageBasis(specType) === PercentageBasis.FixedHundred ? '%' : specType;

/**
 * Threshold rows derive their bound (lowerBound vs. upperBound) from the spec's shared
 * contracted value based on which side of it a row's adjustmentMode falls on — the row's own
 * contracted spec is the lower bound when its adjustmentMode matches the spec's natural
 * higher-value effect, and the upper bound when it opposes it — e.g. a Premium row on Moisture
 * (naturally Discount-effect) is bounded by its upper bound, while a Premium row on BTU (naturally
 * Premium-effect) is bounded by its lower bound.
 */
export const rowUsesLowerBoundAsContractedSpec = (adjustmentMode: AdjustmentMode, specType: SpecType): boolean =>
  adjustmentMode === getDefaultAdjustmentModeFromSpecType(specType);
